import {
  EntityEvent,
  EntityType,
  EntryType,
  InteractionTimelineEvent,
  InteractionType,
} from '@maximizer/core/shared/domain';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { Helper } from '../classes/iqboost';
import { TranslateService } from '@ngx-translate/core';

export const REFERENCE_LINK_CLASS = 'reference-link';

// Investment and insurance records are not CRM entities: a citation of one has nothing to open,
// so it is erased from the answer.
const IGNORED_REFERENCE_TYPE_FRAGMENTS: readonly string[] = [
  'datahub_insurance',
  'datahub_investments',
  'insurance',
  'investments',
];

export function isIgnoredReferenceType(type: string | undefined): boolean {
  if (!type) {
    return false;
  }
  const lower = type.toLowerCase();

  return IGNORED_REFERENCE_TYPE_FRAGMENTS.some((fragment) =>
    lower.includes(fragment),
  );
}

/** The record a rendered citation points at, read back from its data attributes. */
export function createEntityFromReferenceElement(
  target: HTMLElement,
): EntityEvent | null {
  const key = target.getAttribute(DATA_ATTRIBUTES.KEY);
  const type = target.getAttribute(DATA_ATTRIBUTES.TYPE);
  if (!key || !type) {
    return null;
  }

  const entity: EntityEvent = {
    key,
    type: type.toLowerCase() as EntityType,
  };

  const withId = target.getAttribute(DATA_ATTRIBUTES.WITH_ID);
  const withType = target.getAttribute(DATA_ATTRIBUTES.WITH_TYPE);
  if (withId && withType) {
    const interactionDirection = target.getAttribute(
      DATA_ATTRIBUTES.INTERACTION_DIRECTION,
    );
    const interactionType = target.getAttribute(
      DATA_ATTRIBUTES.INTERACTION_TYPE,
    );
    const interactionExternal = target.getAttribute(
      DATA_ATTRIBUTES.INTERACTION_EXTERNAL,
    );

    entity.with = {
      id: withId,
      type: withType as EntryType,
      display: '',
      isNew: false,
    };

    if (interactionDirection && interactionType && interactionExternal) {
      (entity as InteractionTimelineEvent).interaction = {
        direction: interactionDirection,
        type: interactionType,
        external: interactionExternal.toLowerCase() === 'true',
        icon: 0,
        display: '',
      } as InteractionType;
    }
  }

  return entity;
}
export const ENTITY_KEY_PARAM = 'entityKey';
export const WITH_ENTITY_KEY_PARAM = 'withEntityKey';

// Prompts now ask for `key`; stored answers carry `entityKey`. Both resolve, since a citation that
// does not resolve is erased from the answer.
export const ENTITY_KEY_PARAMS: readonly string[] = [ENTITY_KEY_PARAM, 'key'];
export const DATA_ATTRIBUTES = {
  KEY: 'data-key',
  TYPE: 'data-type',
  WITH_ID: 'data-with-id',
  WITH_TYPE: 'data-with-type',
  INTERACTION_DIRECTION: 'data-interaction-direction',
  INTERACTION_TYPE: 'data-interaction-type',
  INTERACTION_EXTERNAL: 'data-interaction-external',
  DATE: 'data-date',
} as const;

/** The citation's own key, under whichever of {@link ENTITY_KEY_PARAMS} the answer used. */
export function readEntityKey(params: Record<string, string>): string {
  for (const name of ENTITY_KEY_PARAMS) {
    if (params[name]) {
      return params[name];
    }
  }

  return '';
}

export function escapeHtmlAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll(/\r\n|\r|\n/g, '&#10;');
}

// The citation comes from markup the markdown pipe already escaped once: decoding first keeps an
// `&amp;` from turning into `&amp;amp;`.
const escapeCitationValue = (value: string | undefined): string =>
  escapeHtmlAttribute(AiMarkupUtils.decodeMarkupEscaping(value ?? ''));

export function createReferenceLink(
  params: Record<string, string>,
  onFormatTemplate: string,
  translate: TranslateService,
): string {
  const key = readEntityKey(params);
  const withId = params[WITH_ENTITY_KEY_PARAM] ?? '';
  const matchingCRMAction = Helper.getCRMActionFromVariation(params['type'] ?? '');
  const translatedType = matchingCRMAction?.result?.typeTranslationKey
    ? translate.instant(matchingCRMAction.result.typeTranslationKey)
    : translate.instant('types.entity');
  const label = onFormatTemplate
    .replace('{0}', translatedType)
    .replace('{1}', params['date'] || '');

  return `
    <span class='${REFERENCE_LINK_CLASS} subtitle-2 text-link inline'
      ${DATA_ATTRIBUTES.KEY}='${escapeCitationValue(key)}'
      ${DATA_ATTRIBUTES.TYPE}='${escapeCitationValue(params['type'])}'
      ${DATA_ATTRIBUTES.WITH_ID}='${escapeCitationValue(withId)}'
      ${DATA_ATTRIBUTES.WITH_TYPE}='${escapeCitationValue(params['withType'])}'
      ${DATA_ATTRIBUTES.INTERACTION_DIRECTION}='${escapeCitationValue(params['interactionDirection'])}'
      ${DATA_ATTRIBUTES.INTERACTION_TYPE}='${escapeCitationValue(params['interactionType'])}'
      ${DATA_ATTRIBUTES.INTERACTION_EXTERNAL}='${escapeCitationValue(params['interactionExternal'])}'
      ${DATA_ATTRIBUTES.DATE}='${escapeCitationValue(params['date'])}'
    >${escapeCitationValue(label)}</span>
  `;
}
